/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];

    const featureId = feature.getID();
    const SCREENS = feature.getSettingValue('SCREENS');
    const BACKCOLOR = feature.getSettingValue('BACKCOLOR');
    const FORECOLOR = feature.getSettingValue('FORECOLOR');
    const IMAGES = feature.getSettingValue('IMAGES');
    const GROUPS = feature.getSettingValue('GROUPS');


    const lcdGraphId = feature.project.getFeature('lcd_graph')?.getID();
    const backgroundColors = [];

    let screensTemplate = '';
    for (let i = 0; i < SCREENS.length; i++) {
        const screen = SCREENS[i];
        const widgets = screen.getSettingValue('WIDGETS');
        backgroundColors.push(screen.getSettingValue('BACKCOLOR'));
        if (widgets.length === 0) {
            continue;
        }
        const screenBlocks = screen.getCodeBlocks();
        blocks.push(...screenBlocks);
        const screenId = screen.getID();
        screensTemplate += ` {
            ID: "${screenId}",
            BACKCOLOR: "${backgroundColors[i]}",
        },
        `;

        blocks.push({
            location: 'display_widgets',
            blockType: BlockType.CODE,
            template: `
if (screenIndex === ${i}) {
    /****display_widgets_${i}****/
}
            `,
        });


        if (lcdGraphId && i === 0) {
            blocks.push({
                location: 'boot',
                blockType: BlockType.CODE,
                template: `
this.updateContext("${screenId}", {
    ...this.context["${screenId}"],
    WIDGETS: [
        ...this.context["${screenId}"].WIDGETS,
        "${lcdGraphId}",
    ],
});`,
            });
        }
    }

    let imagesTemplate = '';
    for (let i = 0; i < IMAGES.length; i++) {
        const image = IMAGES[i];
        const imageId = image.getID();
        const NAME = image.getSettingValue('NAME');
        const IMG = image.getSettingValue('IMG');
        const WIDTH = image.getSettingValue('WIDTH');
        const HEIGHT = image.getSettingValue('HEIGHT');
        imagesTemplate += `
${imageId}: {
    NAME: "${NAME}",
    IMG: "${IMG.split('\n')[1]}",
    WIDTH: ${WIDTH},
    HEIGHT: ${HEIGHT},
},
        `;
    }

    let menuBootTemplate = '';
    const nodeLCDMenu = feature.project.getFeature('node_lcd_menu');
    if (nodeLCDMenu) {
        blocks.push({
            location: 'button_triggered',
            blockType: BlockType.CODE,
            template: `
    if (simulator.context["${featureId}"].MENU_OPEN) {
        const {
            QUIT_CONFIRMATION,
            GROUP_CURSOR,
            SETTING_CURSOR,
            GROUPS,
            SELECTED_GROUP_INDEX,
            SELECTED_SETTING_INDEX,
            EDITING_SETTING_INDEX,
            EDITING_SETTING_VALUE,
        } = simulator.context["${featureId}"];
        const setting = GROUPS[SELECTED_GROUP_INDEX || 0]?.SETTINGS[SETTING_CURSOR || 0];
        if (state === PL_KP_EVENT_PRESSED && button === LCD_KEYPAD_1) {
            if (QUIT_CONFIRMATION) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    QUIT_CONFIRMATION: false,
                });
                console.log("closing exit confirmation");
                return;
            }
            if (SELECTED_SETTING_INDEX !== null) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    SELECTED_SETTING_INDEX: null,
                    EDITING_SETTING_INDEX: 0,
                    EDITING_SETTING_VALUE: null,
                });
                return;
            }
            if (SELECTED_GROUP_INDEX !== null && GROUPS.length > 1) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    SELECTED_GROUP_INDEX: null,
                    SETTING_CURSOR: 0,
                    GROUP_CURSOR: 0,
                });
                return;
            }
            simulator.updateContext("${featureId}", {
                ...simulator.context["${featureId}"],
                QUIT_CONFIRMATION: true,
            });
            console.log("exit confirmation");
        }
        if (state === PL_KP_EVENT_PRESSED && button === LCD_KEYPAD_2) {
             if (QUIT_CONFIRMATION) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    QUIT_CONFIRMATION: false,
                    GROUP_CURSOR: 0,
                });
                return;
            }
            if (SELECTED_SETTING_INDEX !== null) {
                let newSettingValue;
                if (setting.CONTROL === 'STATIC') {
                    let newIndexValue = EDITING_SETTING_INDEX - 1;
                    if (newIndexValue >= 0) {
                        const optionValue = setting.OPTIONS[newIndexValue].VALUE;
                        simulator.updateContext("${featureId}", {
                            ...simulator.context["${featureId}"],
                            EDITING_SETTING_INDEX: newIndexValue,
                            EDITING_SETTING_VALUE: optionValue,
                        });
                    }
                    return;
                }
                if (EDITING_SETTING_INDEX !== EDITING_SETTING_VALUE.length) {
                    const charCode = EDITING_SETTING_VALUE.charCodeAt(EDITING_SETTING_INDEX);
                    const newCharCode = getNextChar(charCode, setting);
                    const newChar = String.fromCharCode(newCharCode);
                    newSettingValue = EDITING_SETTING_VALUE.slice(0, EDITING_SETTING_INDEX) + newChar + EDITING_SETTING_VALUE.slice(EDITING_SETTING_INDEX + 1);
                } else {
                    newSettingValue = EDITING_SETTING_VALUE + String.fromCharCode(getNextChar(32, setting));
                }
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    EDITING_SETTING_VALUE: newSettingValue,
                });
                return;
            }
            if (SELECTED_GROUP_INDEX !== null) {
                if (SETTING_CURSOR === 0) {
                    return
                }
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    SETTING_CURSOR: SETTING_CURSOR - 1,
                });
                return;
            }
            if (GROUP_CURSOR === 0) {
                return;
            }
            simulator.updateContext("${featureId}", {
                ...simulator.context["${featureId}"],
                GROUP_CURSOR: GROUP_CURSOR - 1,
            });
        }
        if (state === PL_KP_EVENT_PRESSED && button === LCD_KEYPAD_3) {
            if (QUIT_CONFIRMATION) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    QUIT_CONFIRMATION: false,
                    GROUP_CURSOR: 0,
                });
                return;
            }
            if (SELECTED_SETTING_INDEX !== null) {
                let newIndexValue = EDITING_SETTING_INDEX + 1;
                if (setting.CONTROL === 'STATIC') {
                    if (EDITING_SETTING_INDEX < EDITING_SETTING_VALUE.length) {
                        const optionValue = setting.OPTIONS[newIndexValue].VALUE;
                        simulator.updateContext("${featureId}", {
                            ...simulator.context["${featureId}"],
                            EDITING_SETTING_INDEX: newIndexValue,
                            EDITING_SETTING_VALUE: optionValue,
                        });
                    }
                    return;
                }
                if (EDITING_SETTING_INDEX >= EDITING_SETTING_VALUE.length
                || (setting.CONTROL === 'IPCTRL' && EDITING_SETTING_INDEX === EDITING_SETTING_VALUE.length - 1)
                ) {
                    newIndexValue = 0;
                }
                if (setting.CONTROL === 'IPCTRL' && EDITING_SETTING_VALUE[newIndexValue] === '.') {
                    newIndexValue+= 1;
                }
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    EDITING_SETTING_INDEX: newIndexValue,
                });
                return;
            }
            if (SELECTED_GROUP_INDEX !== null) {
                if (SETTING_CURSOR === GROUPS[SELECTED_GROUP_INDEX].SETTINGS.length - 1) {
                    return
                }
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    SETTING_CURSOR: SETTING_CURSOR + 1,
                });
                return;
            }
            if (GROUP_CURSOR === GROUPS.length - 1) {
                return;
            }
            simulator.updateContext("${featureId}", {
                ...simulator.context["${featureId}"],
                GROUP_CURSOR: GROUP_CURSOR + 1,
            });
        }
        if (state === PL_KP_EVENT_PRESSED && button === LCD_KEYPAD_4) {
            if (QUIT_CONFIRMATION) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    QUIT_CONFIRMATION: false,
                    MENU_OPEN: false,
                });
                simulator.updateContext("pat", {
                    pattern: '',
                })
                return;
            }
            if (SELECTED_GROUP_INDEX === null) {
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    SELECTED_GROUP_INDEX: GROUP_CURSOR,
                    SETTING_CURSOR: 0,
                });
                return;
            }
            if (SELECTED_SETTING_INDEX === null) {
                const setting = simulator.context["${featureId}"].GROUPS[SELECTED_GROUP_INDEX].SETTINGS[SETTING_CURSOR];
                let value = simulator.settings[setting.SETTINGID];
                if (setting.CONTROL === 'IPCTRL') {
                    value = convertToEditableIP(value);
                }
                if (setting.CONTROL === 'STATIC') {
                    value = setting.OPTIONS[0].VALUE || '';
                }
                simulator.updateContext("${featureId}", {
                    ...simulator.context["${featureId}"],
                    SELECTED_SETTING_INDEX: SETTING_CURSOR,
                    EDITING_SETTING_INDEX: 0,
                    EDITING_SETTING_VALUE: value.toString(),
                });
                return;
            }
            const settingId = setting.SETTINGID;
            let value = EDITING_SETTING_VALUE.replaceAll(' ', '');
            if (setting.CONTROL === 'IPCTRL') {
                value = convertToIP(value);
            }
            stg_set(settingId, value);
            simulator.updateContext("${featureId}", {
                ...simulator.context["${featureId}"],
                EDITING_SETTING_VALUE: null,
                EDITING_SETTING_INDEX: 0,
                SELECTED_SETTING_INDEX: null,
            });
            return;
        }
        return;
    }
            `,
        });

        blocks.push({
            location: 'FUNCTION_DEFINITIONS',
            blockType: BlockType.CODE,
            template: `
function convertToEditableIP(value) {
    if (value.length === 0) {
        value = '0.0.0.0';
    }
    const parts = value.split('.');
    const newParts = [];
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        newParts.push(part.padStart(3, '0'));
    }
    return newParts.join('.');
}

function convertToIP(value) {
    const parts = value.split('.');
    const newParts = [];
    for (let i = 0; i < parts.length; i++) {
        let part = parts[i].replace(/^0+/, '');
        if (part === '') {
            part = '0';
        }
        newParts.push(part);
    }
    return newParts.join('.');
}

function getNextChar(ch, setting) {
    const control = setting.CONTROL;
    const schema = settingsSchema[setting.SETTINGID];
    switch (ch) {
        // space
        case 32:
            if (schema.type === 'F') {
                return 48;
            }
            // A
            return 65;
        // 9
        case 57:
            if (control === 'IPCTRL') {
                return 48;
            }
            // .
            return 46;
        // Z
        case 90:
            // a
            return 97;
        // z
        case 122:
            // 0
            return 48;
        // .
        case 46:
            // space
            return 32;
        default:
            return ch + 1;
    }
}
        `,
        });

        let groupsTemplate = '';
        for (let i = 0; i < GROUPS.length; i++) {
            const GROUPNAME = GROUPS[i].getSettingValue('NAME');
            groupsTemplate += `
{
    NAME: "${GROUPNAME}",
    SETTINGS: [
            `;
            const group = GROUPS[i];
            const settings = group.getSettingValue('SETTINGS');
            for (let j = 0; j < settings.length; j++) {
                const setting = settings[j];
                const SETTINGID = setting.getSettingValue('SETTINGID');
                const stgSetting = feature.project.getFeature(SETTINGID).getSettingValue('SETTINGID');
                const DISPLAYNAME = setting.getSettingValue('NAME');
                const CONTROL = setting.getSettingValue('CONTROL');
                const READONLY = setting.getSettingValue('READONLY');
                let optionsTemplate = '';
                if (CONTROL === 'STATIC') {
                    optionsTemplate = `
OPTIONS: [
    `;
                    const OPTIONS = setting.getSettingValue('OPTIONS');
                    for (let k = 0; k < OPTIONS.length; k++) {
                        const option = OPTIONS[k];
                        const LABEL = option.getSettingValue('LABEL');
                        const VALUE = option.getSettingValue('VALUE');
                        optionsTemplate += `
{
    LABEL: "${LABEL}",
    VALUE: "${VALUE}",
},
                        `;
                    }
                    optionsTemplate += `
],
`;
                }
                groupsTemplate += `
    {
        SETTINGID: "${stgSetting}",
        NAME: "${DISPLAYNAME}",
        CONTROL: "${CONTROL}",
        READONLY: "${READONLY}",
        ${optionsTemplate}
    },
            `;
            }
            groupsTemplate += `
    ],
},
            `;
        }

        menuBootTemplate = `
MENU_OPEN: false,
QUIT_CONFIRMATION: false,
GROUP_CURSOR: 0,
SETTING_CURSOR: 0,
SELECTED_GROUP_INDEX: ${GROUPS.length === 1 ? '0' : 'null'},
SELECTED_SETTING_INDEX: null,
EDITING_SETTING_VALUE: null,
EDITING_SETTING_INDEX: 0,
GROUPS: [
    ${groupsTemplate}
    ],
`;
    }


    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
this.updateContext("${featureId}", {
    currentScreenIndex: 0,
    BACKCOLOR: "${backgroundColors[0]}",
    FORECOLOR: "${FORECOLOR}",
    IMAGES: {
        ${imagesTemplate}
    },
    SCREENS: [
        ${screensTemplate}
    ],
    ${menuBootTemplate}
})
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
function draw_text(element, VISIBLE, TEXT, X, Y, COLOR, ) {
    const {
        ID,
        W,
        H,
        BACKGROUND_COLOR,
        FONT,
        TEXT_ALIGNMENT,
        SCREEN_INDEX,
    } = element;
    simulator.updateContext(ID, {
        TEXT: TEXT || element.TEXT,
        X: X || element.X,
        Y: Y || element.Y,
        COLOR: COLOR || element.COLOR,
        BACKGROUND_COLOR,
        FONT,
        TEXT_ALIGNMENT,
        VISIBLE: VISIBLE || element.VISIBLE,
        SCREEN_INDEX,
    })
}
        `,
    });

    return blocks;
};
